import { Inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, get, includes, pick, times, without } from 'lodash';
import {
  asyncScheduler,
  filter as rxFilter,
  first,
  firstValueFrom,
  map as rxMap,
  Observable,
  Subscription,
  take,
} from 'rxjs';

import {
  DynamicFormExpressions,
  DynamicFormExtension,
  DynamicFormHooks,
  DynamicFormsExtensionsService,
} from '@lockton-apps/dynamic-forms';
import {
  ApplicationConfigStep,
  ApplicationStep,
  ClientTypeType,
  Contact,
  CurrencyConfig,
  Partners,
  ProposalQuickRaterQuotePayload,
  Quote,
} from '@lockton-apps/shared/interfaces';
import { ProposalLocalFacade } from '../../+state/proposal-local.facade';
import { ProposalRaterComponent } from '@lockton-apps/shared/ui/proposal-rater';
import { PROPOSAL_PARTNER_DATA } from '../../constants/proposal-partner-data';
import { ProposalStepActions } from '@lockton-apps/proposals/action';
import {
  AccaClassOfBusinessId,
  CobMetaService,
  NorwayClassOfBusinessId,
} from '@lockton-apps/shared/util/constants';
import { ProposalProductDetailsComponent } from '@lockton-apps/shared/ui/proposal-product-details';
import { FormlySelectOption } from '@ngx-formly/core/select/select-options.pipe';
import {
  AdditionalProductsDetailsService,
  ProposalBuilderService,
} from '@lockton-apps/shared/util/proposal-utils';
import { ProposalsStepCustomizerService } from '../proposals-step-customizer.service';
import { AbstractControl } from '@angular/forms';
import { isAfter, isBefore, parseISO, subYears } from 'date-fns';
import {
  CastObservablePipe,
  Currency2Pipe,
} from '@lockton-apps/shared/ui/pipes';
import {
  DocumentsService,
  SupportingDocumentsService,
} from '@lockton-apps/shared/data-access/documents';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';

interface PartnerData {
  proposalId: string;
  allProductIds: string[];
  clientId: number;
  clientType: ClientTypeType;
  clientName: string;
  orgNumber: string;
  contact?: Contact;
  portalId: Partners;
  configSteps: ApplicationConfigStep[];
  proposalSteps: ApplicationStep[];
  selectedProducts: AccaClassOfBusinessId[];
  currencyConfig: CurrencyConfig;
  readonly: boolean;
  quote: Quote;
}

@Injectable()
export class NorwayDynamicFormsExtensionsService extends DynamicFormsExtensionsService {
  private additionalProductsControl!: AbstractControl;

  constructor(
    @Inject(PROPOSAL_PARTNER_DATA)
    protected readonly partnerData$: Observable<PartnerData>,
    private readonly store: Store,
    private readonly localFacade: ProposalLocalFacade,
    private readonly stepCustomizer: ProposalsStepCustomizerService,
    private readonly currency2Pipe: Currency2Pipe,
    private readonly proposalBuilder: ProposalBuilderService,
    private readonly additionalProductsDetails: AdditionalProductsDetailsService,
    private readonly cobMetaService: CobMetaService,
    castObservable: CastObservablePipe,
    documentsService: DocumentsService,
    supportingDocumentsService: SupportingDocumentsService,
    feedbackService: FeedbackService
  ) {
    super(
      castObservable,
      documentsService,
      supportingDocumentsService,
      feedbackService
    );
  }

  override readonly extensions: DynamicFormExtension[] = [
    {
      id: 'additionalProducts',
      prop: 'options',
      value: this.partnerData$.pipe(
        rxFilter(Boolean),
        rxMap(({ allProductIds, selectedProducts, portalId }) =>
          this.additionalProductsDetails.getDetails<FormlySelectOption>(
            allProductIds,
            selectedProducts,
            portalId,
            ({ id, title }) => ({ value: id, label: title })
          )
        )
      ),
    },
    {
      id: 'productDetails',
      prop: 'component',
      value: ProposalProductDetailsComponent,
    },
    {
      id: 'productDetailsInputs',
      prop: 'inputs',
      value: '',
      mappedValue: () => ({
        cobs: this.partnerData$.pipe(
          rxFilter(Boolean),
          rxMap(({ allProductIds, selectedProducts, portalId }) =>
            this.additionalProductsDetails.getDetails<
              NorwayClassOfBusinessId,
              NorwayClassOfBusinessId
            >(allProductIds, selectedProducts, portalId, (_, cob) => cob)
          )
        ),
      }),
    },
    {
      id: 'rater',
      prop: 'component',
      value: ProposalRaterComponent,
    },
    {
      id: 'raterInputs',
      prop: 'inputs',
      value: '',
      mappedValue: (field) => ({
        productMeta: pick(
          this.cobMetaService.getClassOfBusinessMetaById(
            field.props?.['inputs']?.productId
          ),
          ['title', 'icon', 'color']
        ),
        clientId: this.partnerData$.pipe(
          rxFilter(Boolean),
          rxMap(({ clientId }) => clientId)
        ),
        portalId: this.partnerData$.pipe(
          rxFilter(Boolean),
          rxMap(({ portalId }) => portalId)
        ),
        quote: this.localFacade.quotes$.pipe(
          rxMap((quotes) => {
            const productId: number = get(field.props, 'inputs.productId');

            return quotes[productId];
          })
        ),
        loading: this.localFacade.loadingIds$.pipe(
          rxMap((ids) => includes(ids, get(field.props, 'inputs.productId')))
        ),
        currencyConfig: this.partnerData$.pipe(
          rxFilter(Boolean),
          rxMap(({ currencyConfig }) => currencyConfig)
        ),
      }),
    },
    {
      id: 'raterOutputs',
      prop: 'outputs',
      value: {
        changed: (v: ProposalQuickRaterQuotePayload) =>
          this.localFacade.getQuickRaterQuote(v),
        removed: (id: string) => {
          this.additionalProductsControl.setValue(
            without(this.additionalProductsControl.value, id)
          );
        },
      },
    },
    {
      id: 'sickLeaveDailyAllowanceOptions',
      prop: 'options',
      value: this.partnerData$.pipe(
        rxMap((data) => {
          const options = this.generateSickLeaveDailyAllowanceOptions(
            500,
            5500,
            100,
            data.currencyConfig
          );

          const dob = data.contact?.additionalFields.dateOfBirth;

          const creationDate = parseISO(
            data.contact?.createdDate ?? Date.now().toString()
          );

          const isOver60 =
            dob && isBefore(parseISO(dob), subYears(new Date(), 60));

          const isCreatedAfterDeadline = isAfter(
            creationDate,
            new Date('2024-08-14T00:00:00Z')
          );

          const hasSickPayLimited = isOver60 && isCreatedAfterDeadline;

          return hasSickPayLimited
            ? filter(options, (option) => option.value <= 2500)
            : options;
        })
      ),
    },
    {
      id: 'sickLeaveLengthOfCompensation',
      prop: 'options',
      value: this.partnerData$.pipe(
        rxMap((data) => {
          const dob = data.contact?.additionalFields.dateOfBirth;

          if (!dob || isAfter(parseISO(dob), subYears(new Date(), 60))) {
            return [
              {
                label: '12 måneder',
                value: 12,
              },
              {
                label: '24 måneder',
                value: 24,
              },
            ];
          }

          return [
            {
              label: '12 måneder',
              value: 12,
            },
            {
              label: '24 måneder',
              value: 24,
              disabled: true,
            },
          ];
        })
      ),
    },
    {
      id: 'groupLifeSumInsuredOptions',
      prop: 'options',
      value: this.partnerData$.pipe(
        rxMap((data) => {
          const dob = data.contact?.additionalFields.dateOfBirth;

          if (!dob || isAfter(parseISO(dob), subYears(new Date(), 65))) {
            return [
              {
                label: '12G livsforsikring',
                value: '12_0',
              },
              {
                label: '22G livsforsikring',
                value: '22_0',
              },
              {
                label: '50G livsforsikring',
                value: '50_0',
              },
              {
                label: '12G livsforsikring og 12G uførekapital',
                value: '12_12',
              },
              {
                label: '22G livsforsikring og 12G uførekapital',
                value: '22_12',
              },
              {
                label: '22G livsforsikring og 22G uførekapital',
                value: '22_22',
              },
              {
                label: '50G livsforsikring og 12G uførekapital',
                value: '50_12',
              },
              {
                label: '50G livsforsikring og 22G uførekapital',
                value: '50_22',
              },
              {
                label: '50G livsforsikring og 40G uførekapital',
                value: '50_40',
              },
            ];
          }

          return [
            {
              label: '12G livsforsikring',
              value: '12_0',
            },
            {
              label: '22G livsforsikring',
              value: '22_0',
            },
            {
              label: '50G livsforsikring',
              value: '50_0',
            },
          ];
        })
      ),
    },
  ];

  override readonly hooks: DynamicFormHooks[] = [
    {
      id: 'additionalProducts',
      hookConfig: {
        onInit: (field) => {
          this.additionalProductsControl = field.formControl as AbstractControl;
        },
        afterViewInit: (field) => {
          if (this.schedulers.has('hooks.additionalProducts.afterViewInit')) {
            (
              this.schedulers.get(
                'hooks.additionalProducts.afterViewInit'
              ) as Subscription
            ).unsubscribe();
          }

          this.schedulers.set(
            'hooks.additionalProducts.afterViewInit',
            asyncScheduler.schedule(() => {
              field.formControl?.valueChanges
                .pipe(
                  take(1),
                  concatLatestFrom(() => this.partnerData$)
                )
                .subscribe(
                  ([
                    addons,
                    {
                      proposalId,
                      configSteps,
                      proposalSteps,
                      clientType,
                      selectedProducts,
                    },
                  ]) => {
                    const { steps, data } = this.proposalBuilder.rebuildSteps(
                      proposalSteps,
                      configSteps,
                      selectedProducts,
                      field.model,
                      addons,
                      this.stepCustomizer.customize,
                      clientType
                    );

                    this.localFacade.setAdditionalProductsIds(addons);
                    this.store.dispatch(
                      ProposalStepActions.additionalProductsSet({
                        proposalId,
                        application: { steps, data, addons },
                      })
                    );
                  }
                );
            })
          );
        },
      },
    },
    {
      id: 'defaultStartDate',
      hookConfig: {
        onInit: (field) => {
          field.options?.formState.partnerData.defaultStartDate$
            .pipe(
              rxFilter(
                (defaultStartDate) =>
                  !!defaultStartDate && !field.formControl?.value
              ),
              take(1)
            )
            .subscribe((defaultStartDate: string) => {
              field.formControl?.setValue(defaultStartDate);
            });
        },
      },
    },
    {
      id: 'clientName',
      hookConfig: {
        onInit: async (field) => {
          if (!field.formControl) {
            return;
          }

          if (!field.formControl.value) {
            const data: PartnerData = await firstValueFrom(
              this.partnerData$.pipe(first())
            );

            field.formControl.setValue(data.clientName);
          }
        },
      },
    },
    {
      id: 'clientName_dynamic',
      hookConfig: {
        onInit: async (field) => {
          if (!field.formControl) {
            return;
          }

          const hasValue: boolean = !!field.formControl.value;
          const isFirstInstance: boolean =
            field.formControl.root.value.length === 1;

          if (!hasValue && isFirstInstance) {
            const data: PartnerData = await firstValueFrom(
              this.partnerData$.pipe(first())
            );

            field.formControl.setValue(data.clientName);
          }
        },
      },
    },
    {
      id: 'orgNumber_dynamic',
      hookConfig: {
        onInit: async (field) => {
          if (!field.formControl) {
            return;
          }

          const hasValue: boolean = !!field.formControl.value;
          const isFirstInstance: boolean =
            field.formControl.root.value.length === 1;

          if (!hasValue && isFirstInstance) {
            const data: PartnerData = await firstValueFrom(
              this.partnerData$.pipe(first())
            );

            field.formControl.setValue(data.orgNumber);
          }
        },
      },
    },
  ];

  override expressions: Record<string, DynamicFormExpressions> = {
    startDateOptions: {
      'props.datepickerOptions.min': (field) => {
        return field.options?.formState.partnerData?.isContact
          ? 'calc=now|add|{"days":1}'
          : null;
      },
      'props.datepickerOptions.max': (field) => {
        return field.options?.formState.partnerData?.isContact
          ? 'calc=now|add|{"months":1}'
          : null;
      },
    },
  };

  private generateSickLeaveDailyAllowanceOptions(
    min: number,
    max: number,
    step: number,
    currencyConfig: CurrencyConfig
  ): FormlySelectOption[] {
    return times((max - min) / step + 1, (index) => {
      const value = min + step * index;

      return {
        value,
        label: `${this.currency2Pipe.transform(value, currencyConfig)}`,
      };
    });
  }
}
